import {Loader2} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import type {MemberInfo, AddressInfo} from './types';
import {MEMBER_ONLY_FIELDS} from './types';
import {formatGenderLabel} from '@common/gender';
import {maskSsn} from '@common/lib/utils';

type ReviewConfirmStepProps = {
  member: MemberInfo;
  address: AddressInfo;
  groupId: string;
  coverageEffectiveDate: string;
  // Review is step 4 in the Champ flow (with product steps) and step 3 elsewhere.
  stepNumber: number;
  allSelectedProductIds: string[];
  getProductName: (id: string) => string;
  isCreating: boolean;
  onBack: () => void;
  onCreate: () => void;
};

function formatMemberValue(key: string, value: string): string {
  if (key === 'ssn') return maskSsn(value);
  if (key === 'sexCode') return formatGenderLabel(value) ?? value;
  return value;
}

export function ReviewConfirmStep({
  member,
  address,
  groupId,
  coverageEffectiveDate,
  stepNumber,
  allSelectedProductIds,
  getProductName,
  isCreating,
  onBack,
  onCreate,
}: ReviewConfirmStepProps) {
  const nameDisplay = [member.firstName, member.middleName, member.lastName]
    .filter(Boolean)
    .join(' ');

  const reviewFields = MEMBER_ONLY_FIELDS.filter(
    (f) => !['firstName', 'lastName', 'middleName'].includes(f.key),
  );

  return (
    <>
      <Card className='border-0 shadow-lg bg-white overflow-hidden mb-6'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Step {stepNumber}: Review & Confirm
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
            <div>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3'>
                Member Info
              </h4>
              <div className='space-y-1.5 text-sm'>
                <p>
                  <span className='text-gray-400'>Name:</span> {nameDisplay}
                </p>
                {reviewFields.map((field) => {
                  const value = member[field.key as keyof MemberInfo];
                  if (!value) return null;
                  return (
                    <p key={field.key}>
                      <span className='text-gray-400'>{field.label}:</span>{' '}
                      {formatMemberValue(field.key, value)}
                    </p>
                  );
                })}
              </div>
            </div>
            <div>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3'>
                Group & Coverage
              </h4>
              <div className='space-y-1.5 text-sm'>
                <p>
                  <span className='text-gray-400'>Group ID:</span> {groupId}
                </p>
                <p>
                  <span className='text-gray-400'>Coverage Effective:</span> {coverageEffectiveDate}
                </p>
              </div>

              {(address.street || address.city || address.state || address.zip) && (
                <>
                  <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 mt-6'>
                    Address
                  </h4>
                  <div className='space-y-1.5 text-sm'>
                    {address.street && <p>{address.street}</p>}
                    {address.street2 && <p>{address.street2}</p>}
                    <p>
                      {[address.city, address.state].filter(Boolean).join(', ')}
                      {address.zip ? ` ${address.zip}` : ''}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {allSelectedProductIds.length > 0 && (
            <div>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3'>
                Products
              </h4>
              <div className='space-y-2'>
                {allSelectedProductIds.map((id) => (
                  <div
                    key={id}
                    className='flex items-center justify-between p-3 rounded-lg bg-gray-50'
                  >
                    <span className='text-sm font-medium text-brand-navy'>
                      {getProductName(id)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className='flex items-center justify-between'>
        <Button variant='outline' onClick={onBack}>
          Back
        </Button>
        <Button onClick={onCreate} disabled={isCreating}>
          {isCreating && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
          Create Member
        </Button>
      </div>
    </>
  );
}
